import type { AdminRevenueDay } from "@/api/admin";

type DonutProps = {
  segments: { label: string; value: number; color: string }[];
  centerLabel: string;
  centerValue: string;
};

export function DonutChart({ segments, centerLabel, centerValue }: DonutProps) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  const r = 42;
  const c = 2 * Math.PI * r;

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
      <div className="relative h-36 w-36 shrink-0">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r={r} fill="none" stroke="#eef0f3" strokeWidth="12" />
          {segments.map((seg) => {
            const len = (seg.value / total) * c;
            const el = (
              <circle
                key={seg.label}
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth="12"
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="text-lg font-bold tabular-nums">{centerValue}</div>
          <div className="text-[11px] text-ink-muted">{centerLabel}</div>
        </div>
      </div>
      <ul className="space-y-2 text-sm">
        {segments.map((seg) => (
          <li key={seg.label} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: seg.color }} />
            <span className="text-ink-muted">{seg.label}</span>
            <span className="ml-auto font-medium tabular-nums">{seg.value.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type TrendProps = {
  days: AdminRevenueDay[];
  metric: "wallet_inr_paise" | "promo_inr_paise" | "signups";
};

const metricLabels: Record<TrendProps["metric"], string> = {
  wallet_inr_paise: "Wallet top-ups (₹)",
  promo_inr_paise: "Promotions (₹)",
  signups: "New users",
};

export function TrendChart({ days, metric }: TrendProps) {
  const values = days.map((d) => Number(d[metric] ?? 0));
  const max = Math.max(...values, 1);
  const w = 320;
  const h = 120;
  const pad = 8;
  const step = days.length > 1 ? (w - pad * 2) / (days.length - 1) : 0;

  const points = values
    .map((v, i) => {
      const x = pad + i * step;
      const y = h - pad - (v / max) * (h - pad * 2);
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div>
      <div className="mb-2 text-xs font-medium text-ink-muted">{metricLabels[metric]}</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="h-28 w-full">
        <polyline
          fill="none"
          stroke="#2d68c4"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
        />
        {values.map((v, i) => {
          const x = pad + i * step;
          const y = h - pad - (v / max) * (h - pad * 2);
          return <circle key={i} cx={x} cy={y} r="3" fill="#2d68c4" />;
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-ink-muted">
        {days.map((d) => (
          <span key={d.day}>{d.day.slice(5)}</span>
        ))}
      </div>
    </div>
  );
}
